<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue';
import { router } from '@inertiajs/vue3';
import { storeToRefs } from 'pinia';
import { useOrderStore } from '@/stores/order';
import { useCompoundStore } from '@/stores/compound';
import { useOptionsStore } from '@/stores/options';
import { useUserStore } from '@/stores/user';
import { useToast } from 'vue-toastification';
import { isRole } from '@/utils/roleHelper';
import { useDebounce } from '@/composables/debounce';

const { profile } = storeToRefs(useUserStore());
const optionsStore = useOptionsStore();
const { compoundTypeOptions, labProducts } = storeToRefs(optionsStore);
const orderStore = useOrderStore();
const { cart } = storeToRefs(orderStore);
const { submit, calculate } = orderStore;
const compoundStore = useCompoundStore();
const { searchLabCompounds, favoriteLabCompound, unfavoriteLabCompound } = compoundStore;
const { labCompounds } = storeToRefs(compoundStore);
const compoundFavoriteLoadingId = ref(null);
const toast = useToast();
const loading = ref(false);
const roles = computed(() => isRole(profile.value));
const { debounce } = useDebounce();
const customOrderUrl = 'https://lemarchanddispensary.ca/orders/';

const openCustomOrder = () => {
    window.open(customOrderUrl, '_blank', 'noopener,noreferrer');
};

const props = defineProps({
    order: {
        type: Object,
        default: null
    }
});

const note = ref('');
const rxNumber = ref('');

const activeLab = computed(() => {
    if (profile.value?.pharmacy?.lab) { // For pharmacy users, use the lab from pharmacy
        return profile.value.pharmacy.lab;
    } else if (profile.value?.active_lab) { // For system admin and super admin users, use the active lab
        return profile.value.active_lab;
    } else if (profile.value?.lab) { // For lab users, use the lab from profile
        return profile.value.lab;
    }
    return null;
});

const activePharmacy = computed(() => {
    if (profile.value?.pharmacy) { // For pharmacy users, use the pharmacy from profile
        return profile.value.pharmacy;
    } else if (profile.value?.active_pharmacy) { // For system admin, super admin and lab users, they are accessing the pharmacy, use the active pharmacy
        return profile.value.active_pharmacy;
    }
    return null;
});

const isAdmin = computed(() => {
    return roles.value.superAdmin() || roles.value.systemAdmin();
});

const orderCalculations = ref({
    pharmacy_delivery_rate: 0,
    delivery_fee: 0,
    compound_breakdown: [],
    total: 0,
    rebate_fee: 0,
});

const baseProducts = computed(() =>
    labProducts.value
        .filter(product => product.type === 'Base')
        .sort((a, b) => a.label.localeCompare(b.label))
);

const activeIngredientProducts = computed(() =>
    labProducts.value
        .filter(product => product.type === 'Active')
        .sort((a, b) => a.label.localeCompare(b.label))
);

const initSearch = ref(false);
const orderIsQuote = ref(false);
const tempQuantities = ref({});
const quantityErrors = ref({});
const activeIngredientPercentages = reactive({});

const activeIngredientLabel = (productId) => {
    const product = activeIngredientProducts.value.find(
        (p) => Number(p.value) === Number(productId)
    );
    return product?.label ?? String(productId);
};

const buildSearchPayload = () => {
    const payload = { labHash: activeLab.value.hash, ...params.value };
    const activeIds = payload.active_ingredient_product_id;
    delete payload.active_ingredient_product_id;

    // if users input active ingredient in the search, add them to the
    // payload as product_id: percentage
    if (activeIds && activeIds.length > 0) {
        payload.active_ingredient_filters = activeIds.map((id) => {
            const entry = { product_id: Number(id) };
            const percentage = activeIngredientPercentages[id];

            if (percentage !== null && percentage !== '' && percentage !== undefined) {
                entry.percentage = Number(percentage);
            }

            return entry;
        });
    }

    return payload;
};

const searchCompounds = () => {
    searchLabCompounds(buildSearchPayload());
};

const toggleFavorite = async (compound) => {
    if (!activeLab.value?.hash || !compound.hash) {
        return;
    }
    compoundFavoriteLoadingId.value = compound.id;
    const payload = buildSearchPayload();
    const { labHash, ...query } = payload;
    const action = compound.is_favorite ? unfavoriteLabCompound : favoriteLabCompound;
    try {
        await action({
            labHash,
            compoundHash: compound.hash,
            ...query
        });
    } catch (e) {
        toast.error(e?.response?.data?.message ?? 'Could not update favourite.');
    } finally {
        compoundFavoriteLoadingId.value = null;
    }
};

const clearSearch = () => {
    params.value.term = '';
    searchCompounds();
};
const formatPrice = value => {
    return new Intl.NumberFormat('en-CA', {
        style: 'currency',
        currency: 'CAD',
    }).format(value);
};

// Expiry is stored on the compound as a number of days.
const hasExpiry = expiry => {
    return expiry !== null && expiry !== undefined && expiry !== '';
};

const cartCompoundIds = computed(() => new Set(cart.value.map((item) => item.compound.id)));

/** Compounds from the current search page that are not already in the cart. */
const labCompoundsVisibleListing = computed(() => {
    const rows = labCompounds.value.data ?? [];
    return rows.filter((c) => !cartCompoundIds.value.has(c.id));
});

const clearQuantityError = (compoundId) => {
    quantityErrors.value[compoundId] = null;
};

const quantityValidationMessage = (value) => {
    const quantity = Number(value);

    if (!Number.isFinite(quantity) || quantity <= 0) {
        return 'Please enter a valid quantity greater than 0.';
    }

    if (!/^\d+(?:\.\d{1,2})?$/.test(String(value).trim())) {
        return 'Please enter a valid quantity with no more than 2 decimal places.';
    }

    return null;
};

const backendErrorMessage = (error, fallback) => {
    const errors = error?.response?.data?.errors;
    if (errors) {
        return Object.values(errors).flat()[0] ?? fallback;
    }

    return error?.response?.data?.message ?? fallback;
};

const formatQuantity = (value) => Number(value).toFixed(2);

const addToCart = (compound) => {
    const quantityInput = tempQuantities.value[compound.id];
    const errorMessage = quantityValidationMessage(quantityInput);

    if (errorMessage) {
        quantityErrors.value[compound.id] = errorMessage;
        toast.error(errorMessage);
        return;
    }

    cart.value.push({
        compound: { ...compound },
        quantity: formatQuantity(quantityInput)
    });
    tempQuantities.value[compound.id] = null;
    quantityErrors.value[compound.id] = null;
    recalculateOrderTotal();
    toast.success(`${compound.name} added to cart.`);
};

const removeFromCart = index => {
    cart.value.splice(index, 1);
    recalculateOrderTotal();
};

const submitOrder = () => {
    if (cart.value.length === 0) {
        toast.error('Please select at least one compound before submitting the order.');
        return;
    }

    const payload = {
        cart: [...cart.value],
        pharmacyId: activePharmacy.value.id,
        orderIsQuote: orderIsQuote.value,
        note: note.value,
        rx_number: rxNumber.value,
        rebate_fee: orderCalculations.value.rebate_fee,
        rebate_fee_percent: activePharmacy.value.rebate_fee_percent,
    };

    loading.value = true;

    submit(payload)
        .then((resp) => {
            toast.success('Order submitted successfully! You will receive a confirmation email shortly.');
            router.visit(`/orders/${resp.data.order.hash}/details`);
        })
        .catch(error => {
            toast.error(backendErrorMessage(error, 'The order could not be submitted. Please review the quantities and try again.'));
            console.error(error);
        }).finally(() => {
            loading.value = false;
            cart.value = []; // Clear the cart
        });
};

const recalculateOrderTotal = () => {
    calculate({
        cart: [...cart.value],
        pharmacy_hash: activePharmacy.value.hash,
    }).then(resp => {
        orderCalculations.value = resp.data;
    }).catch(error => {
        toast.error(backendErrorMessage(error, 'The order total could not be calculated. Please review the quantities.'));
        console.error('Error recalculating order total:', error);
    });
};

// Total number of pages
const totalPages = computed(() => {
    return labCompounds.value.last_page;
});

watch(profile, () => {
    if (activeLab.value.hash && !initSearch.value) {
        initSearch.value = true;
        searchCompounds();
    }
});

onMounted(() => {
    const isReorder = new URLSearchParams(window.location.search).has('order_hash');

    if (props.order && Array.isArray(props.order.compounds)) {
        if (isReorder) {
            cart.value = [];
        }
        props.order.compounds.forEach(compound => {
            cart.value.push({
                compound: { ...compound },
                quantity: compound.pivot.quantity
            });
        });
        recalculateOrderTotal();
        return;
    }

    if (!isReorder && cart.value.length > 0) {
        recalculateOrderTotal();
    }
});

const params = ref({
    term: '',
    page: 1,
    sort_by: null,
    per_page: 5,
    compound_type_id: [],
    base_product_id: [],
    active_ingredient_product_id: [],
});

watch(
    () => params.value.active_ingredient_product_id,
    (ids) => {
        const idSet = new Set((ids ?? []).map((id) => Number(id)));
        Object.keys(activeIngredientPercentages).forEach((key) => {
            if (!idSet.has(Number(key))) {
                delete activeIngredientPercentages[key];
            }
        });
    },
    { deep: true }
);

const debouncedSearchCompounds = () => {
    debounce(searchCompounds, { delay: 500 });
};
</script>

<template>
<MainContentLayout>
    <template #breadcrumb>
        <BreadCrumb />
    </template>
    <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 px-4 md:px-12 mb-4">
        <h1 class="text-3xl font-bold">
            {{ $t("label.select_compounds") }}
        </h1>
        <StyledButton
            class="self-start md:self-auto"
            @click="openCustomOrder">
            {{ $t("label.custom_order") }}
        </StyledButton>
    </div>
    <div class="grid grid-cols-1 md:grid-cols-3 items-start gap-4 px-4 md:px-24 mb-12">
        <StyledSearch
            v-model="params.term"
            v-debounce:500ms="searchCompounds"
            name="search"
            :label="$t('label.search_multi_term_hint')"
            :debounce-events="['input']"
            :loading="loading"
            @click:clear="clearSearch" />
        <StyledDropdown
            v-model="params.compound_type_id"
            clearable
            searchable
            :placeholder="$t('label.compound_type')"
            :options="compoundTypeOptions"
            name="compound_type"
            :loading="loading"
            @click:clear="searchCompounds"
            @input="searchCompounds" />
        <StyledAutoComplete
            v-model="params.base_product_id"
            class="self-start"
            clearable
            multiple
            :label="$t('label.base')"
            :placeholder="$t('label.base')"
            :options="baseProducts"
            name="base_product"
            :loading="loading"
            @click:clear="searchCompounds"
            @input="searchCompounds" />
        <div class="flex flex-col gap-3">
            <StyledAutoComplete
                v-model="params.active_ingredient_product_id"
                clearable
                multiple
                :label="$t('label.active_allow_multiple')"
                :placeholder="$t('label.active_ingredient')"
                :options="activeIngredientProducts"
                name="active_ingredient_product"
                :loading="loading"
                @click:clear="searchCompounds"
                @input="searchCompounds" />
            <div
                v-if="params.active_ingredient_product_id?.length"
                class="flex flex-col gap-3">
                <div
                    v-for="productId in params.active_ingredient_product_id"
                    :key="productId"
                    class="flex flex-col gap-2">
                    <span class="text-sm font-medium text-gray-800">
                        {{ activeIngredientLabel(productId) }}
                    </span>
                    <v-text-field
                        v-model="activeIngredientPercentages[productId]"
                        class="max-w-xs"
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        density="compact"
                        variant="outlined"
                        hide-details
                        suffix="%"
                        :loading="loading"
                        :label="$t('label.active_ingredient_percent')"
                        :placeholder="$t('label.active_ingredient_percent_placeholder')"
                        clearable
                        @update:modelValue="debouncedSearchCompounds"
                        @click:clear="searchCompounds" />
                </div>
            </div>
        </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 px-4 md:px-12 lg:px-24">
        <div class="lg:col-span-2 min-w-0">
            <!-- All lab compounds -->
            <transition-group
                name="list"
                tag="div"
                class="grid grid-cols-1 gap-4">
                <div
                    v-for="compound in labCompoundsVisibleListing"
                    :key="compound.id"
                    class="p-4 md:p-8 rounded-xl shadow-lg transition-all duration-300">
                    <!-- Compound Title + favourite -->
                    <div class="flex flex-row justify-between items-start gap-2 w-full">
                        <div class="text-lg md:text-xl font-bold flex-1 min-w-0">
                            {{ compound.name }} <span v-if="compound.is_sterile">({{ $t("label.sterile") }})</span>
                        </div>
                        <v-btn
                            icon
                            variant="text"
                            density="compact"
                            class="shrink-0"
                            :loading="compoundFavoriteLoadingId === compound.id"
                            aria-label="Toggle favourite"
                            @click.stop="toggleFavorite(compound)">
                            <FontAwesomeIcon
                                :icon="compound.is_favorite ? ['fas', 'star'] : ['far', 'star']"
                                class="text-xl"
                                :class="compound.is_favorite ? 'text-amber-700' : 'text-amber-400'" />
                        </v-btn>
                    </div>
                    <div class="mt-1 text-sm md:text-base text-gray-600">
                        {{ $t("label.expiry") }}:
                        <template v-if="hasExpiry(compound.expiry)">
                            {{ compound.expiry }} {{ $t("label.days") }}
                        </template>
                        <template v-else>{{ $t("label.n_a") }}</template>
                    </div>
                    <div class="mt-2">{{ compound.compound_type.display_name }}</div>
                    <details class="mt-2">
                        <summary class="font-semibold cursor-pointer">
                            {{ $t("label.details") }}
                        </summary>
                        <div class="mt-2">
                            <ul class="list-disc pl-5">
                                <li v-for="product in compound.products" :key="product.id" class="text-sm">
                                    <div v-if="product.pivot.percentage != 0">{{ product.name }} - {{ product.pivot.percentage }}%</div>
                                    <div v-if="Number(product.pivot.quantity) != 0">{{ product.name }} - {{ product.pivot.quantity }}{{ product.unit.name }}</div>
                                </li>
                            </ul>
                        </div>
                    </details>
                    <div class="mt-4">
                        <div class="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                            <div class="w-full sm:w-40">
                                <v-text-field
                                    v-model="tempQuantities[compound.id]"
                                    type="number"
                                    inputmode="decimal"
                                    min="0.01"
                                    step="0.01"
                                    variant="outlined"
                                    density="compact"
                                    hide-details
                                    :error="Boolean(quantityErrors[compound.id])"
                                    :loading="loading"
                                    :placeholder="$t('qty')"
                                    @update:model-value="clearQuantityError(compound.id)" />
                            </div>
                            <div class="h-10 w-full sm:w-auto">
                                <v-btn
                                    color="green"
                                    size="small"
                                    class="w-full sm:w-auto"
                                    style="height: 100%;"
                                    :loading="loading"
                                    @click="addToCart(compound)">
                                    {{ $t("label.add_to_cart") }}
                                </v-btn>
                            </div>
                        </div>

                        <!-- Display quantity error message if present -->
                        <div
                            v-if="quantityErrors[compound.id]"
                            class="mt-1 text-sm text-red-600 sm:whitespace-nowrap">
                            {{ quantityErrors[compound.id] }}
                        </div>
                    </div>
                </div>
                <div v-if="(labCompounds.data?.length ?? 0) === 0" :key="'empty-state'" class="text-center text-red-600 font-semibold py-4">
                    {{ $t("label.cant_find_compound") }}
                    <a
                        :href="customOrderUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-blue-600 underline">
                        {{ $t("label.click_here") }}
                    </a>
                    {{ $t("label.to_fill_out_manual_order_form") }}
                </div>
                <div
                    v-else-if="labCompoundsVisibleListing.length === 0"
                    :key="'empty-cart-page'"
                    class="text-center text-gray-700 font-semibold py-4">
                    {{ $t("label.compounds_on_page_in_cart") }}
                </div>
                <v-pagination
                    :key="'pagination'"
                    v-model="params.page"
                    density="compact"
                    :length="totalPages"
                    @click="searchCompounds" />
            </transition-group>
        </div>

        <!-- Cart Summary -->
        <div class="lg:col-span-1 min-w-0">
            <div class="bg-white p-4 md:p-6 rounded-xl shadow-lg">
                <h2 class="text-xl md:text-2xl font-bold mb-4">{{ $t("label.your_cart") }}</h2>
                <li v-for="(item, index) in cart" :key="`${item.compound.id}-${index}`" class="flex flex-row items-center justify-between border-b py-2">
                    <div class="text-base md:text-lg">
                        <span class="font-medium">{{ item.compound.name }}</span>
                        <span class="ml-2 text-gray-600 text-sm md:text-base">x{{ formatQuantity(item.quantity) }}</span>
                    </div>
                    <div class="flex items-center gap-4 pl-4">
                        <v-icon color="red" :loading="loading" @click="removeFromCart(index)">mdi-delete</v-icon>
                    </div>
                </li>
                <div class="mt-4">
                    <div class="flex justify-between">
                        <span class="font-medium">{{ $t("label.total") }}:</span>
                        <span>{{ formatPrice(orderCalculations.total) }}</span>
                    </div>
                </div>
                <v-checkbox
                    v-model="orderIsQuote"
                    :label="$t('label.order_is_quote')"
                    class="-ml-2"
                    :loading="loading" />
                <div class="mb-4">
                    <v-text-field
                        v-model="rxNumber"
                        :label="$t('label.rx_number')"
                        :loading="loading"
                        clearable />
                </div>
                <div v-if="!orderIsQuote">
                    <v-textarea
                        v-model="note"
                        :label="$t('label.note')"
                        :loading="loading"
                        rows="3"
                        class="mb-4" />
                </div>
                <v-btn
                    block
                    color="green"
                    large
                    class="mt-4"
                    :loading="loading"
                    @click="submitOrder">
                    {{ $t("label.submit_order") }}
                </v-btn>
                <div class="mt-4 text-md text-grey-700 text-left">
                    {{ $t('label.pricing_fluctuation_notice') }}
                </div>
                <div v-if="isAdmin" class="mt-12">
                    <v-expansion-panels multiple>
                        <v-expansion-panel
                            v-for="compound in orderCalculations.compound_breakdown"
                            :key="compound.compound_id"
                            class="mb-4">
                            <v-expansion-panel-title>
                                <span
                                    class="font-medium">{{ $t("label.name") }}:</span>
                                <span class="ml-2">{{
                                    compound['Compound Name']
                                }} <span v-if="compound.is_sterile">({{ $t("label.sterile") }})</span></span>
                            </v-expansion-panel-title>
                            <v-expansion-panel-text>
                                <div
                                    v-for="(value, key) in compound"
                                    :key="key"
                                    class="flex gap-4">
                                    <span class="font-medium w-48">{{
                                        key
                                    }}:</span>
                                    <span>
                                        {{
                                            (() => {
                                                switch (true) {
                                                case key.includes('Price') || key.includes('Fee') || key.includes('Amount') || key.includes('Total') || key == 'Embedded Markup:' || key == 'Rx Markup:':
                                                    return `${formatPrice(value)}`;
                                                case key.includes('Rx Markup %') || key.includes('Embedded Markup %'):
                                                    return `${value}%`;
                                                default:
                                                    return typeof value === 'number'
                                                        ? value.toFixed
                                                            ? value.toFixed(2)
                                                            : value
                                                        : value || $t("label.not_available");
                                                }
                                            })()
                                        }}
                                    </span>
                                </div>
                            </v-expansion-panel-text>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </div>
            </div>
        </div>
    </div>
</MainContentLayout>
</template>
